import { Button, Space, Spin, Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import _ from 'lodash';
import React, { ReactElement, ReactNode, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import ILoan from '~Api/Loan/ILoan';
import { IDictionary } from '~utilities/IDictionary';
import ILoanDrawdown from '~Api/Loan/ILoanDrawdown';
import dayjs from 'dayjs';
import LoanDrawdownStatusEnum from '~Api/Loan/LoanDrawdownStatusEnum';
import Layout from '~/Loans/Loan/Layout';
import {
    loanDrawdownsListAction,
    loanGetAction,
} from '~Loans/actions';
import { IGlobalState } from '~reducer';
import {
    loanDrawdownsSelector,
    loanSelector,
} from '~Loans/selectors';
import ModalStateEnum from '~UI/ModalStateEnum';
import DrawdownAddModal from './DrawdownAddModal';
import LoanDrawdownStatusTag from './LoanDrawdownStatusTag';
import './loan-drawdowns.less';
import { currencyFormatter } from '~utilities/formatters';

export default function List(): ReactElement {
    const { loanUuid } = useParams<{ loanUuid: string }>();

    const [ isAddModalOpen, setIsAddModalOpen ] = useState<ModalStateEnum>(ModalStateEnum.Off);

    const loan: ILoan = useSelector((state: IGlobalState): ILoan => loanSelector(state, loanUuid));
    const drawdowns: IDictionary<ILoanDrawdown> = useSelector((state: IGlobalState): IDictionary<ILoanDrawdown> => loanDrawdownsSelector(state, loanUuid));

    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (!loan) {
            dispatch(loanGetAction(loanUuid));
        }
    }, [
        dispatch,
        loan,
        loanUuid,
    ]);

    useEffect((): void => {
        if (!drawdowns) {
            dispatch(loanDrawdownsListAction(loanUuid));
        }
    }, [
        dispatch,
        drawdowns,
        loanUuid,
    ]);

    const onClickCreate: () => void = useCallback((): void => {
        setIsAddModalOpen(ModalStateEnum.Open);
    }, []);

    const onCloseAddModal: () => void = useCallback((): void => {
        setIsAddModalOpen(ModalStateEnum.Closed);
    }, []);

    if (!loan || !drawdowns) {
        return (
            <Layout uuid={loanUuid} section='list'>
                <Typography.Title level={2}>Drawdowns</Typography.Title>
                <Spin />
            </Layout>
        );
    }

    const columns: ColumnsType<ILoanDrawdown> = [
        {
            dataIndex: 'drawdownNumber',
            render: (drawdownNumber: string, loanDrawdown: ILoanDrawdown): ReactNode => <Link to={`/loans/${loanUuid}/drawdowns/${loanDrawdown.uuid}`}>{drawdownNumber}</Link>,
            title: 'Drawdown Number',
        },
        {
            dataIndex: 'requestedTime',
            defaultSortOrder: 'descend',
            render: (requestedTime: string): ReactNode => dayjs(requestedTime).format('DD/MM/YYYY'),
            sorter: (a: ILoanDrawdown, b: ILoanDrawdown): number => dayjs(a.requestedTime) > dayjs(b.requestedTime) ? 1 : -1,
            title: 'Requested Date',
            width: '20%',
        },
        {
            dataIndex: 'processedTime',
            render: (processedTime: string): ReactNode => processedTime ? dayjs(processedTime).format('DD/MM/YYYY') : '-',
            title: 'Disbursed Date',
            width: '20%',
        },
        {
            render: (loanDrawdown: ILoanDrawdown): ReactNode => {
                if ([
                    LoanDrawdownStatusEnum.PendingApproval,
                    LoanDrawdownStatusEnum.Approved,
                    LoanDrawdownStatusEnum.DisbursalInstructed,
                    LoanDrawdownStatusEnum.Processed,
                ].includes(loanDrawdown.status)) {
                    return currencyFormatter.format(
                        (loanDrawdown.retainedContingencyAmount ?? 0) + (loanDrawdown.retainedFundsAmount ?? 0),
                    );
                }

                return currencyFormatter.format(loanDrawdown.requestedAmount);
            },
            title: 'Amount',
            width: '10%',
        },
        {
            render: (loanDrawdown: ILoanDrawdown): ReactNode => <LoanDrawdownStatusTag loanDrawdown={loanDrawdown} />,
            title: 'Status',
            width: '10%',
        },
    ];

    const addModalBlock: JSX.Element = ModalStateEnum.Off !== isAddModalOpen && (
        <DrawdownAddModal
            loan={loan}
            isOpen={ModalStateEnum.Open === isAddModalOpen}
            onCancel={onCloseAddModal}
        />
    );

    const drawdownActionsBlock: JSX.Element = _.filter(drawdowns, (drawdown: ILoanDrawdown): boolean => ![
        LoanDrawdownStatusEnum.Cancelled,
        LoanDrawdownStatusEnum.Processed,
    ].includes(drawdown.status)).length === 0 && (
        <Space className='actions'>
            <Button onClick={onClickCreate}>Create Drawdown</Button>
        </Space>
    );

    return (
        <Layout uuid={loanUuid} section='list'>
            {drawdownActionsBlock}
            <Typography.Title className='title' level={2}>Drawdowns</Typography.Title>
            <Table
                columns={columns}
                dataSource={_.values(drawdowns)}
                pagination={false}
                rowKey='uuid'
                size='middle'
            />
            {addModalBlock}
        </Layout>
    );
}
